import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="product-form"
export default class extends Controller {
  static targets = [
    "quantityInput", "incrementButton", "decrementButton",
    "bottomPrice", "topPrice", "originalPrice", "ingredientCheckbox",
    "bottomSavings", "availability", "cta"
  ]
  // Lo que ya está en mi carrito de este producto (editando, sin el ítem editado): el tope
  // del selector es el disponible que llega por broadcast menos esto. update: ficha en modo
  // actualizar (storefront_quick_add.md): el CTA no se apaga sin disponible, porque bajar siempre pasa.
  static values = { inCart: { type: Number, default: 0 }, update: { type: Boolean, default: false } }

  connect() {
    this.isProcessing = false
    this.stepperAdditionalPrice = 0
    this.currencyFormatter = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 })

    this.basePrice = 0
    if (this.hasBottomPriceTarget) {
      this.basePrice = parseFloat(this.bottomPriceTarget.dataset.price || 0)
    } else if (this.hasOriginalPriceTarget) {
      this.basePrice = parseFloat(this.originalPriceTarget.dataset.price || 0)
    }

    this.calculateIngredientPrice()
    this.updateBottomBarPrice()
    this.toggleQuantityButtons()
    this.bindCheckboxListeners()
    this.bindStepperListeners()
    this.element.addEventListener("submit", this.submit.bind(this))
  }

  // Disponibilidad en vivo: el indicador de stock llega por broadcast
  // (products/_availability.turbo_stream) y Stimulus lo reconecta como target. Sincroniza
  // el tope del selector y el CTA con data-available; nunca baja la cantidad ya elegida (el
  // servidor valida al agregar). También corre al cargar, con el número del servidor.
  // Ver _docs/features/products/stock_lifecycle.md → "Disponibilidad en vivo".
  availabilityTargetConnected(element) {
    if (!this.hasQuantityInputTarget) return

    const available = parseInt(element.dataset.available)
    const addable = Number.isNaN(available) ? NaN : Math.max(available - this.inCartValue, 0)
    if (Number.isNaN(addable)) {
      this.quantityInputTarget.removeAttribute("max")
    } else {
      this.quantityInputTarget.max = addable
    }
    if (this.hasCtaTarget) this.ctaTarget.disabled = addable === 0 && !this.updateValue
    this.toggleQuantityButtons()
  }

  increment() {
    this.changeQuantity("increment")
  }

  decrement() {
    this.changeQuantity("decrement")
  }

  changeQuantity(action) {
    if (this.isProcessing) return
    if (!this.hasQuantityInputTarget) return
    
    this.isProcessing = true

    let quantity = parseInt(this.quantityInputTarget.value)

    const max = parseInt(this.quantityInputTarget.max)
    quantity = action === "increment"
      ? Math.min(quantity + 1, Number.isNaN(max) ? Infinity : max)
      : Math.max(quantity - 1, 1)

    this.quantityInputTarget.value = quantity
    this.updateBottomBarPrice()
    this.toggleQuantityButtons()

    this.isProcessing = false
  }

  toggleQuantityButtons() {
    if (!this.hasQuantityInputTarget || !this.hasDecrementButtonTarget) return
    
    const quantity = parseInt(this.quantityInputTarget.value)
    this.decrementButtonTarget.disabled = quantity <= 1

    const max = parseInt(this.quantityInputTarget.max)
    if (this.hasIncrementButtonTarget) {
      this.incrementButtonTarget.disabled = !Number.isNaN(max) && quantity >= max
    }
  }

  calculateIngredientPrice() {
    if (!this.hasIngredientCheckboxTarget) {
      this.ingredientPrice = 0
      return
    }
    
    this.ingredientPrice = this.ingredientCheckboxTargets
      .filter(cb => cb.dataset.allowRepeat !== "true" && parseInt(cb.value) > 0)
      .reduce((sum, cb) => sum + parseFloat(cb.dataset.price || 0), 0)
  }

  updateBottomBarPrice() {
    if (!this.hasQuantityInputTarget || !this.hasBottomPriceTarget) return

    const quantity = parseInt(this.quantityInputTarget.value) || 1
    const totalPrice = (this.basePrice + this.ingredientPrice + this.stepperAdditionalPrice) * quantity
    this.bottomPriceTarget.textContent = `$${this.currencyFormatter.format(Math.round(totalPrice))}`

    // Pill "Ahorras": el descuento aplica solo al precio base del producto
    // (los ingredientes no tienen descuento), así que escala con la cantidad
    if (this.hasBottomSavingsTarget) {
      const unitSavings = parseFloat(this.bottomSavingsTarget.dataset.unitSavings || 0)
      this.bottomSavingsTarget.textContent = `Ahorras $${this.currencyFormatter.format(Math.round(unitSavings * quantity))}`
    }
  }

  bindCheckboxListeners() {
    // Listen for price change events from ingredient-limit controllers (checkbox mode)
    this.element.addEventListener("ingredient-limit:priceChange", () => {
      this.calculateIngredientPrice()
      this.updateBottomBarPrice()
    })
  }

  bindStepperListeners() {
    // Listen for price change events from ingredient-qty controllers
    this.element.addEventListener("ingredient-qty:priceChange", () => {
      this.recalcStepperPrice()
      this.updateBottomBarPrice()
    })
  }

  recalcStepperPrice() {
    // Sum additional prices from all ingredient-qty controllers
    let total = 0
    const qtyControllers = this.element.querySelectorAll("[data-controller*='ingredient-qty']")
    qtyControllers.forEach(el => {
      const ctrl = this.application.getControllerForElementAndIdentifier(el, "ingredient-qty")
      if (ctrl) total += ctrl.calculateAdditionalPrice()
    })
    this.stepperAdditionalPrice = total
  }

  submit(event) {
    const missingCategories = []

    // Validate stepper-based categories (allow_repeat)
    const qtyControllers = this.element.querySelectorAll("[data-controller*='ingredient-qty']")
    qtyControllers.forEach(el => {
      const ctrl = this.application.getControllerForElementAndIdentifier(el, "ingredient-qty")
      if (ctrl) {
        const err = ctrl.validate()
        if (err) missingCategories.push(err)
      }
    })

    // Validate checkbox-based categories
    // Use a composite key (comboInstance + categoryId) so each combo instance
    // is validated independently
    if (this.hasIngredientCheckboxTarget) {
      const requiredCategories = {}

      this.ingredientCheckboxTargets.forEach(cb => {
        if (cb.dataset.allowRepeat !== "true" && cb.dataset.required === "true") {
          const instancePrefix = cb.dataset.comboInstance || ""
          const key = `${instancePrefix}||${cb.dataset.categoryId}`
          const label = instancePrefix
            ? `${instancePrefix} — ${cb.dataset.categoryName}`
            : cb.dataset.categoryName
          requiredCategories[key] = label
        }
      })

      const selectedKeys = new Set()

      this.ingredientCheckboxTargets.forEach(cb => {
        if (cb.dataset.allowRepeat !== "true" && parseInt(cb.value) > 0 && cb.dataset.required === "true") {
          const instancePrefix = cb.dataset.comboInstance || ""
          const key = `${instancePrefix}||${cb.dataset.categoryId}`
          selectedKeys.add(key)
        }
      })

      for (const key in requiredCategories) {
        if (!selectedKeys.has(key)) {
          missingCategories.push(requiredCategories[key])
        }
      }
    }

    if (missingCategories.length > 0) {
      event.preventDefault()
      alert(`Debes seleccionar al menos un ingrediente en: ${missingCategories.join(', ')}.`)
    }
  }
  
  
}
